import React from 'react';
import styled from '@emotion/styled';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import P1 from '@base/P1/P1';
import H6 from '@base/H6/H6';
import { Spacings } from '@styles/index';
import Button from '@base/Button/Button';
import Image from '@base/Image';
import MUIDialog from '@material-ui/core/Dialog';
import Slide from '@material-ui/core/Slide';
import cookies from '@utils/cookies';
import { segment } from '@actions/analytics';
import { SYLLABUS_CLICKED_KEYS, NOTES } from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { evtNameCategoryMap } from '@constants/index';
import { NOTES_GET_STARTED_CLICKED } from '@cont/EpubNotes/utils';

interface IFeatureIntroductionProps {
  goalUID: string;
}

const Transition = React.forwardRef(function Transition(props, ref) {
  // @ts-ignore
  return <Slide direction="up" ref={ref} {...props} />;
});

const Wrapper = styled.div`
  padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_6B} ${Spacings.SPACING_8B}
    ${Spacings.SPACING_6B};
`;

const Title = styled.div`
  font-size: ${Spacings.SPACING_7B};
  font-weight: 600;
  line-height: ${Spacings.SPACING_9B};
  margin-top: ${Spacings.SPACING_4B};
  margin-bottom: ${Spacings.SPACING_1B};
  color: var(--color-base-fill);
`;

const StyP1 = styled(P1)`
  max-width: 384px;
  line-height: 18px;
`;

const StyH6 = styled(H6)`
  line-height: 18px;
`;

const ItemsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${Spacings.SPACING_4B};
  margin-top: ${Spacings.SPACING_6B};
  margin-bottom: ${Spacings.SPACING_8B};
`;

const Item = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${Spacings.SPACING_3B};
`;

const StyDialog = styled(MUIDialog)`
  &.MuiDialog-root .MuiDialog-container .MuiPaper-root {
    bottom: 0;
    background: var(--color-base-0);
    border-radius: ${Spacings.SPACING_4B};
    max-height: none;
    width: 464px;
    box-shadow: ${boxShadowGenerator({ color: 'rgba(0, 0, 0, 0.12)' })};
  }
  &.MuiDialog-root {
    overflow: scroll;
  }
`;

const InnerWrapper = styled.div`
  padding-left: ${Spacings.SPACING_4B};
`;

const StyButton = styled(Button)`
  font-size: 14px;
  border-radius: ${Spacings.SPACING_1B};
  color: var(--color-base-1);
  height: ${Spacings.SPACING_10B};
  width: 140px;
`;

const Images = styled.div`
  position: relative;
`;

const CloseIcon = styled.div`
  position: absolute;
  cursor: pointer;
  top: 0;
  right: 0;
  margin-top: ${Spacings.SPACING_4B};
  margin-right: ${Spacings.SPACING_4B};
`;

const FeatureIntroduction: React.FC<IFeatureIntroductionProps> = ({
  goalUID
}) => {
  const [open, setOpen] = React.useState(true);
  const goalSegmentData = useGoalEventProps({ goalUID });
  const closeDialog = () => {
    setOpen(false);
    cookies.setCookie('notesPageVisited', true, 180);
    segment.track(
      NOTES_GET_STARTED_CLICKED,
      {
        ...goalSegmentData,
        last_primary_source: NOTES,
        last_primary_source_section: 'NOTES - Feature Introduction'
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      SYLLABUS_CLICKED_KEYS
    );
  };

  return (
    <StyDialog
      open={open}
      // @ts-ignore
      TransitionComponent={Transition}
    >
      <Wrapper>
        <Images>
          <Image
            src="notes/feature-introduction.png"
            width="416px"
            height="227px"
            alt="notes"
          />
          <CloseIcon onClick={closeDialog}>
            <Image
              src="syllabus/close-icon.svg"
              width={Spacings.SPACING_7B}
              height={Spacings.SPACING_7B}
              alt="notes"
            />
          </CloseIcon>
        </Images>
        <InnerWrapper>
          <Title>Notes</Title>
          <StyP1 color="var(--color-text-secondary)">
            In-depth notes covering your entire syllabus
          </StyP1>
          <ItemsWrapper>
            <Item>
              <Image
                src="notes/search.png"
                width={Spacings.SPACING_6B}
                height={Spacings.SPACING_6B}
                alt="notes"
              />
              <StyH6>Quickly search for anything in the notes</StyH6>
            </Item>
            <Item>
              <Image
                src="notes/theme.png"
                width={Spacings.SPACING_6B}
                height={Spacings.SPACING_6B}
                alt="notes"
              />
              <StyH6>Pick a light or dark theme to read comfortably</StyH6>
            </Item>
          </ItemsWrapper>
          <StyButton
            // @ts-ignore
            label="Get started"
            color="black"
            maxHeight="xs"
            width="90px"
            onClick={closeDialog}
          />
        </InnerWrapper>
      </Wrapper>
    </StyDialog>
  );
};

export default FeatureIntroduction;
